import { useTheme, Grid } from '@mui/material';
import FullCalendar, { EventContentArg } from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { forwardRef, useContext } from 'react';
import { AgentSchedule } from '@nice-ccf/core-sdk';
import schedulerStyles from './ccf-scheduler.styles';
import {
  CcfTranslationKey,
  TranslatorContext,
  useTranslator,
} from '../../ccf-translator/ccf-translator';

/**
 * @remarks - AgentScheduleForDisplay extends agentsSchedule and adds few optional properties namely display, className, desccription,
 * borderHighlightColor for customization of events and shifts.
 */
export interface AgentScheduleForDisplay extends AgentSchedule {
  /**
   * @remarks - controls which preset rendering style events use, Possible values: auto, block, list-item, background, inverse-background, none
   */
  display?: string;
  /**
   * @remarks - determines which HTML classNames will be attached to the rendered event.
   */
  className?: string;
  /**
   * @remarks - shift/activity notes
   */
  description?: string;
  /**
   * @remarks - left-aligned highlighted activity color
   */
  borderHighlightColor?: string;
  /**
   * @remarks - shift/activity notes(max 500 characters)
   */
  isWEMEvent?: boolean;
}
export interface CcfSchedulerProps {
  events: AgentScheduleForDisplay[];
  initialView?: string;
  height?: string | number;
  nowIndicator?: boolean;
  headerToolbar?: any;
  dayMaxEvents?: number;
  slotDuration?: string;
  scrollTime?: string;
  firstDay?: number;
  allDaySlot?: boolean;
  allDayText?: CcfTranslationKey;
  isWemView?: boolean;
  /**
   * @remarks - renderScheduleContent used for rendering the event details
   **/
  renderScheduleContent?: (eventArgs: EventContentArg) => JSX.Element | '';
}

export const CcfScheduler = forwardRef((props: CcfSchedulerProps, ref: any) => {
  const [translator] = useTranslator();
  const {
    events,
    initialView = props.isWemView ? 'timeGridWeek' : 'timeGridDay',
    height = '100%',
    nowIndicator = true,
    headerToolbar = false,
    slotDuration = '01:00:00',
    scrollTime = '',
    firstDay = 0,
    allDaySlot = false,
    allDayText = 'shift',
    renderScheduleContent,
  } = props;
  const theme = useTheme();
  const styles = schedulerStyles(theme);
  const { locale } = useContext(TranslatorContext);

  return (
    <Grid sx={styles.schedulerStyles}>
      <FullCalendar
        ref={ref}
        locale={locale}
        events={events}
        height={height}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView={initialView}
        nowIndicator={nowIndicator}
        eventContent={renderScheduleContent}
        headerToolbar={headerToolbar}
        dayHeaderFormat={
          // !props?.isWemView
          //   ? {
          //       month: 'short',
          //       day: 'numeric',
          //       year: 'numeric',
          //     }
          //   :
          {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
            omitCommas: true,
          }
        }
        eventTimeFormat={{
          hour: 'numeric',
          minute: '2-digit',
          meridiem: 'lowercase',
        }}
        slotLabelFormat={{
          hour: 'numeric',
          minute: '2-digit',
          omitZeroMinute: true,
          meridiem: 'lowercase',
        }}
        slotDuration={slotDuration}
        slotEventOverlap={false}
        scrollTime={scrollTime}
        firstDay={firstDay}
        eventTextColor="#001633"
        eventBorderColor="#dedede"
        allDaySlot={allDaySlot}
        allDayText={translator(allDayText)}
        eventOrderStrict={true}
        eventOrder={'start'}
      />
    </Grid>
  );
});
